import React, { useEffect, useState } from "react";
import Breadcrumbs from "../../core/breadcrumbs";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { Link } from "react-router-dom";
import { apiClient } from "../../services/apiClient";
import { useToast } from "../../Toaster/ToastContext";
import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";

// Apply the autoTable plugin to jsPDF
applyPlugin(jsPDF);

const InventoryReport = () => {
  const { showToast } = useToast();
  const [inventoryData, setInventoryData] = useState([]);
  const [searchQuery, setSearchQuery] = useState(""); // State for search input

  // Fetch inventory report
  const fetchInventoryReport = async () => {
    try {
      const response = await apiClient.post(`/generateInventoryReport`, {
        type: "preview",
      });
      if (response?.success && Array.isArray(response.data)) {
        setInventoryData(response.data);
      } else {
        setInventoryData([]);
      }
    } catch (error) {
      console.error("Failed to fetch inventory report:", error);
      showToast("Failed to fetch inventory report", "error");
    }
  };

  useEffect(() => {
    fetchInventoryReport();
  }, []);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Filter inventory data based on search query
  const filteredInventoryData = inventoryData.filter((product) => {
    const query = searchQuery.toLowerCase();
    const matchesProduct =
      (product.name?.toLowerCase() || "").includes(query) ||
      (product.sku?.toLowerCase() || "").includes(query) ||
      (product.category_name?.toLowerCase() || "").includes(query);

    const matchesVariant =
      product.variants?.some(
        (variant) =>
          (variant.name?.toLowerCase() || "").includes(query) ||
          (variant.variant_value?.toLowerCase() || "").includes(query) ||
          (variant.sku?.toLowerCase() || "").includes(query)
      ) || false;

    return matchesProduct || matchesVariant;
  });

  // Handle Excel export
  const handleExportExcel = async () => {
    try {
      const response = await apiClient.post(`/generateInventoryReport`, {});
      const fileUrl = response?.path;

      if (fileUrl) {
        const link = document.createElement("a");
        link.href = fileUrl;
        link.download = "inventory-report.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showToast("Inventory report exported to Excel successfully", "success");
      } else {
        showToast("No file URL found in response", "error");
      }
    } catch (error) {
      console.error("Failed to export inventory report to Excel:", error);
      showToast("Failed to export inventory report to Excel", "error");
    }
  };

  // Handle PDF export
  const handleExportPDF = () => {
    try {
      const doc = new jsPDF();

      // Header
      doc.setFontSize(20);
      doc.text("Inventory Report", 14, 20);
      doc.setFontSize(12);
      doc.text(
        `Generated on: ${new Date().toLocaleString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          timeZone: "Asia/Karachi",
        })}`,
        14,
        30
      );

      // Table
      doc.autoTable({
        startY: 40,
        head: [["Product Name", "SKU", "Category", "Unit", "In Stock Qty"]],
        body: filteredInventoryData.flatMap((product) => {
          const rows = [];
          const hasVariants = product.variants && product.variants.length > 0;

          // Main product row
          rows.push([
            product.name || "N/A",
            product.sku || "N/A",
            product.category_name || "N/A",
            product.unit || "N/A",
            product.product_type === "single"
              ? product.quantity || "0"
              : hasVariants
              ? product.variants.reduce(
                  (sum, variant) => sum + (variant.quantity || 0),
                  0
                )
              : "N/A",
          ]);

          // Variant rows
          if (hasVariants) {
            product.variants.forEach((variant) => {
              rows.push([
                `  -> ${variant.name} (${variant.variant_value})` || "N/A",
                variant.sku || "N/A",
                product.category_name || "N/A",
                product.unit || "N/A",
                variant.quantity || "0",
              ]);
            });
          }

          return rows;
        }),
        theme: "grid",
        styles: { fontSize: 10, cellPadding: 3 },
        headStyles: { fillColor: [100, 100, 100], textColor: [255, 255, 255] },
        alternateRowStyles: { fillColor: [240, 240, 240] },
        margin: { top: 40 },
      });

      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.text(`Page ${i} of ${pageCount}`, 190, 285, { align: "right" });
      }

      // Save PDF
      doc.save("inventory-report.pdf");
      showToast("Inventory report exported to PDF successfully", "success");
    } catch (error) {
      console.error("Failed to export PDF:", error);
      showToast("Failed to export inventory report to PDF", "error");
    }
  };

  // Render product rows including variants
  const renderProductRows = () => {
    return filteredInventoryData.flatMap((product) => {
      const rows = [];
      const hasVariants = product.variants && product.variants.length > 0;

      // Main product row
      rows.push(
        <tr key={product.id}>
          <td className="productimgname" style={{ whiteSpace: "nowrap" }}>
            <div className="view-product me-2">
              <Link to="#">
                {product.image && product.image.length > 0 ? (
                  <ImageWithBasePath src={product.image[0]} alt="product" />
                ) : (
                  <ImageWithBasePath
                    src="https://odabill.com/assets/img/product/noimage.png"
                    alt="product"
                  />
                )}
              </Link>
            </div>
            <Link to="#">
              <span className="wrap-text">{product.name || "N/A"}</span>
            </Link>
          </td>
          <td>
            <span className="wrap-text">{product.sku || "N/A"}</span>
          </td>
          <td>
            <span className="wrap-text">{product.category_name || "N/A"}</span>
          </td>
          <td>
            <span className="wrap-text">{product.unit || "N/A"}</span>
          </td>
          <td>
            <span className="wrap-text">
              {product.product_type === "single"
                ? product.quantity || "0"
                : hasVariants
                ? product.variants.reduce(
                    (sum, variant) => sum + (variant.quantity || 0),
                    0
                  )
                : "N/A"}
            </span>
          </td>
        </tr>
      );

      // Variant rows
      if (hasVariants) {
        product.variants.forEach((variant) => {
          rows.push(
            <tr
              key={`${product.id}-variant-${variant.id}`}
              className="variant-row"
            >
              {/* <td></td> */}
              <td className="productimgname" style={{ paddingLeft: "30px" }}>
                <div className="view-product me-2"></div>
                <Link to="#">
                  <span className="wrap-text">
                    ↳ {variant.name} ({variant.variant_value})
                  </span>
                </Link>
              </td>
              <td>
                <span className="wrap-text">{variant.sku || "N/A"}</span>
              </td>
              <td>
                <span className="wrap-text">
                  {product.category_name || "N/A"}
                </span>
              </td>
              <td>
                <span className="wrap-text">{product.unit || "N/A"}</span>
              </td>
              <td>
                <span className="wrap-text">{variant.quantity || "0"}</span>
              </td>
            </tr>
          );
        });
      }

      return rows;
    });
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <Breadcrumbs
          maintitle="Inventory Report"
          subtitle="Manage Your Inventory Report"
          onExcelClick={handleExportExcel}
          onPDFClick={handleExportPDF}
        />
        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top">
              <div className="search-set">
                <div className="search-input">
                  <input
                    type="text"
                    placeholder="Search by name, category and sku "
                    className="form-control form-control-sm formsearch"
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                  <Link to="#" className="btn btn-searchset">
                    <i data-feather="search" className="feather-search" />
                  </Link>
                </div>
              </div>
            </div>
            <div className="table-responsive">
              <table className="table datanew">
                <thead>
                  <tr>
                    <th style={{ width: "30%" }}>Product Name</th>
                    <th style={{ width: "20%" }}>SKU</th>
                    <th style={{ width: "20%" }}>Category</th>
                    <th style={{ width: "15%" }}>Unit</th>
                    <th style={{ width: "10%" }}>In Stock Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInventoryData.length > 0 ? (
                    renderProductRows()
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center">
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryReport;
